import { useEffect, useState, useRef } from "react";
import SideDrawerOverlay from "../SideOverLay";
import star from "../../assets/images/star.svg"
import emptyStar from "../../assets/images/emptyStar.svg";
import { Button } from "../../common/components/Button";
import { Button as MuiButton } from "../components/Common/Button";
import styles from "./index.module.scss";
import { postCall, putCall } from "../../services/apiService";
import { endPoints, PORTAL } from "../../constants/urlConstants";
import { RATING_KEYS, RecommendationLevel } from "../../constants";
import UserCard from "../SeatApprovalFlow/SeekerDetailsOverlay/Common/UserCards";
import GrayLine from "../../common/components/GrayLine";
import CaretCircleDown from "../../assets/images/CaretCircleDown.svg";
import CaretCircleUp from "../../assets/images/CaretCircleUp.svg";
import {
  calculateOverallRating,
  getAverageRating,
} from "../../utils/commonFunctions";
import { getItemInLocalStorage } from "../../services/localStorage";
import { PREVIOUS_RATING_IS, PREVIOUS_RATING_IS_LABEL, PREVIOUS_RATING_LABEL, RecommendationLevelText } from "../../constants/textConstants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../common/components/CustomeSelect";
import { PrevRating } from "../../types/seatApproval";

const ReviewOverlay = ({
  open,
  onClose,
  onSave,
  seekerId,
  programId,
  initialRatings = {},
  initialComments = "",
  seekersData,
  disablePreferences,
  prevRating,
  initialRecommendations,
  onRefresh,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (review: any) => void;
  seekerId?: number;
  programId?: number;
  initialRatings?: { [key: string]: { rating: number; id?: number } };
  initialComments?: string;
  seekersData?: unknown;
  prevRating?: PrevRating;
  disablePreferences;
  initialRecommendations;
  onRefresh?: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const recommendationRef = useRef<HTMLDivElement>(null);
const followUpCountOptions = Array.from({ length: 101 }, (_, i) => i.toString());
  const [form, setForm] = useState({
    prevRatings: initialRatings,
    ratings: initialRatings,
    comments: initialComments,
    recComments: initialRecommendations?.recommendationText || "",
    wouldRecommend:
      initialRecommendations?.isRecommended === true
        ? "yes"
        : initialRecommendations?.isRecommended === false
        ? "no"
        : "",
    recommendation: initialRecommendations?.recommendationKey || "",
    followUpCount: initialRecommendations?.followUpCount ? initialRecommendations.followUpCount.toString() : "",
  });

  useEffect(() => {
    setForm({
      ratings: initialRatings,
      comments: initialComments,
      recComments: initialRecommendations?.recommendationText || "",
      wouldRecommend:
        initialRecommendations?.isRecommended === true
          ? "yes"
          : initialRecommendations?.isRecommended === false
            ? "no"
            : "",
      recommendation: initialRecommendations?.recommendationKey || "",
      followUpCount: initialRecommendations?.followUpCount ? initialRecommendations.followUpCount.toString() : "",
    });
  }, [initialRatings, initialComments, initialRecommendations]);

  useEffect(() => {
    if (form.wouldRecommend === "no") {
      setForm((prev) => ({
        ...prev,
        recommendation: "",
        recComments: "",
        followUpCount: "",
      }));
    }
    else if (form.wouldRecommend === "yes" && recommendationRef.current) {
      setTimeout(() => {
        recommendationRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 150);
    }
  }, [form.wouldRecommend]);

  const showRecommendationRequired =
    (form.wouldRecommend === "yes" && !form.recommendation) || (form.wouldRecommend === "yes" && form.recComments && !form.recommendation);
  const avgRating = getAverageRating(form.ratings, RATING_KEYS.length);
  const isFormValid =
    form.comments.trim().length > 0 &&
    !showRecommendationRequired &&
    avgRating > 0;
    
  const userId = getItemInLocalStorage("seekerDetails")?.id;
  const isUpdate =
    !!initialComments &&
    initialComments.trim().length > 0 &&
    initialRatings &&
    Object.keys(initialRatings).length > 0;

  const resetForm = () => {
    setForm({
      ratings: initialRatings,
      comments: initialComments,
      recComments: initialRecommendations?.recommendationText || "",
      wouldRecommend:
        initialRecommendations?.isRecommended === true
          ? "yes"
          : initialRecommendations?.isRecommended === false
            ? "no"
            : "",
      recommendation: initialRecommendations?.recommendationKey || "",
      followUpCount: initialRecommendations?.followUpCount ? initialRecommendations.followUpCount.toString() : "",
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    let ratingsArr;
    if (isUpdate) {
      ratingsArr = RATING_KEYS.map(({ key }) => ({
        ...(form.ratings[key]?.id ? { id: form.ratings[key].id } : {}),
        ratingKey: key,
        rating: Number(form.ratings[key]?.rating) ?? 0,
      }));
    } else {
      ratingsArr = RATING_KEYS.map(({ key }) => ({
        ratingKey: key,
        rating:
          typeof form.ratings[key]?.rating === "number"
            ? form.ratings[key].rating
            : 0,
      }));
    }

    let recommendationPayload = undefined;
    if (form.wouldRecommend) {
      recommendationPayload = {
        ...(initialRecommendations ? { id: initialRecommendations.id } : {}),
        isRecommended: form.wouldRecommend === "yes",
        recommendationKey:
          form.wouldRecommend === "yes"
            ? form.recommendation
              ? (form.recommendation as RecommendationLevel)
              : null
            : null,
        recommendationText:
          form.wouldRecommend === "yes" ? form.recComments : null,
        followUpCount: form.wouldRecommend === "yes" && form.followUpCount ? Number(form.followUpCount) : null,
        recommendedBy: userId,
      };
    }

    const data: any = {
      ratings: ratingsArr,
      review: form.comments,
    };
    if (recommendationPayload) {
      data.recommendation = recommendationPayload;
    }

    try {
      let response;
      if (isUpdate) {
        response = await putCall(
          endPoints.ratingForRegisteredSeekers(seekerId),
          data,
          PORTAL
        );
      } else {
        response = await postCall(
          endPoints.ratingForRegisteredSeekers(seekerId),
          data,
          PORTAL
        );
      }

      if (response?.status && response.status >= 200 && response.status < 300) {
        onSave({ success: true });
        if (onRefresh) onRefresh();
        resetForm();
        handleClose();
      } else {
        onSave({ success: false });
      }
    } catch (error) {
      onSave({ success: false });
    }
  };

  const recommendationOptions = [
    {
      key: RecommendationLevel.WHOLEHEARTEDLY,
      value: "Wholeheartedly recommended",
    },
    {
      key: RecommendationLevel.AFFIRMATIVELY,
      value: "Affirmatively recommended",
    },
    {
      key: RecommendationLevel.SUPPORTIVELY,
      value: "Supportively recommended",
    },
  ];

  if (!open) return null;

  return (
    <SideDrawerOverlay
      open={open}
      grayLine={true}
      onClose={handleClose}
      headerText={isUpdate ? "Update review" : "Add review"}
      footer={
        <>
          <MuiButton
            variant="outline"
            className={styles.cancelButton}
            onClick={handleClose}
          >
            cancel
          </MuiButton>
          <Button
            buttonTextClassName={styles.saveButtonText}
            buttonClassName={styles.saveButton}
            onClick={handleSave}
            disable={!isFormValid}
          >
            {isUpdate ? "update" : "save"}
          </Button>
        </>
      }
    >
      <div className={styles.ratingForm}>
        {/* Generated by Copilot - Add more robust safety checks for seekersData */}
        {seekersData && 
         typeof seekersData === 'object' && 
         seekersData !== null && 
         Object.keys(seekersData).length > 0 && (
          <UserCard seeker={seekersData} disablePreferences={true} />
        )}
        <div>
          {prevRating?.rating && prevRating.rating !== null && prevRating.rating > 0 && <div className={styles.prevRatingContainer}>
            <div className={styles.prevRatingHeaderContainer}>
              <div className={styles.prevRatingHeader}>
              <span>Previous Rating</span>
              {isOpen ? <img src={CaretCircleUp} alt="Collapse" onClick={() => setIsOpen(false)} className={styles.caretIcon}/> : <img src={CaretCircleDown} alt="Expand" onClick={() => setIsOpen(true)} className={styles.caretIcon}/>}
              </div>
              <GrayLine />
            </div>
            {isOpen && (
              <div className={styles.prevRatingContent}>
              <span className={styles.prevRatingText}>
                {prevRating?.rmContactName && prevRating?.hdbYear
                  ? `${prevRating.rmContactName}'s ${prevRating.hdbYear} ${PREVIOUS_RATING_LABEL}  `
                  : prevRating?.rmContactName
                  ? `${prevRating.rmContactName} ${PREVIOUS_RATING_IS}  `
                  : `${PREVIOUS_RATING_IS_LABEL}  `
                }
                <span className={styles.prevRatingStars}>
                  <span className={styles.starIcon}><img src={star} alt="star" width={13} height={13} /> </span>
                <span className={styles.prevRatingScore}>{prevRating.rating || 0}</span>
               </span>
              </span>
              <p className={styles.prevRatingReview}>{prevRating?.review || ""}</p>
              </div>
            )}
            </div>}
          <div className={styles.filterlabelName}>
            {RecommendationLevelText.rating}
            <GrayLine />
          </div>
          <div className={styles.totalRating}>
            Current rating is{" "}
            <span className={styles.ratingScore}>
              {getAverageRating(form.ratings, RATING_KEYS.length)}
            </span>
          </div>
          {RATING_KEYS.map(({ label, key, rm_display_name }) => (
            <div key={key} className={styles.ratingRow}>
              <span className={styles.filterFieldName}>{rm_display_name}</span>
              <span className={styles.stars}>
                {[1, 2, 3, 4, 5].map((starNum) => (
                  <img
                    key={starNum}
                    src={
                      starNum <= (form.ratings[key]?.rating || 0)
                        ? star
                        : emptyStar
                    }
                    alt=""
                    className={styles.starIcon}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        ratings: {
                          ...prev.ratings,
                          [key]: {
                            ...prev.ratings[key],
                            rating: starNum,
                          },
                        },
                      }))
                    }
                    onDoubleClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        ratings: {
                          ...prev.ratings,
                          [key]: {
                            ...prev.ratings[key],
                            rating: 0,
                          },
                        },
                      }))
                    }
                    style={{
                      cursor: "pointer",
                      width: 24,
                      height: 24,
                      marginRight: 4,
                    }}
                  />
                ))}
              </span>
            </div>
          ))}
        </div>

        <div>
          <span className={styles.filterlabels} style={{ marginTop: 16 }}>
            {RecommendationLevelText.RMReview}
          </span>
          <textarea
            className={styles.textarea}
            rows={4}
            value={form.comments}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, comments: e.target.value }))
            }
          />
        </div>

        <div className={styles.RecommendationHeader}>
          <p className={styles.RecommendationHeaderLabel}>
            {RecommendationLevelText.Recommendation}
          </p>
          <GrayLine />
        </div>

        <div className={styles.RecommendationSection}>
          <span className={styles.filterlabels} style={{ marginTop: 16 }}>
            {RecommendationLevelText.WouldYouLikeToRecommend}
          </span>
          <div className={styles.options}>
            {["yes", "no"].map((opt) => (
              <label className={styles.radioLabel} key={opt}>
                <input
                  type="radio"
                  name="wouldRecommend"
                  value={opt}
                  checked={form.wouldRecommend === opt}
                  onChange={() =>
                    setForm((prev) => ({
                      ...prev,
                      wouldRecommend: opt,
                    }))
                  }
                />
                {opt.charAt(0).toUpperCase() + opt.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {form.wouldRecommend === "yes" && (
          <div ref={recommendationRef}>
             <p className={styles.filterlabels} style={{ marginTop: 16, marginBottom: 16 }}>
              {RecommendationLevelText.FollowUpCount}
            </p>
            <Select
              value={form.followUpCount}
              onValueChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  followUpCount: value,
                }))
              }
            >
              <SelectTrigger className={styles.selectTrigger} style={{ width: "30%", maxWidth: "30%" }}>
                <SelectValue placeholder="Select count" className={styles.selectValue} />
              </SelectTrigger>
              <SelectContent 
                className={styles.selectContent}
                style={{ zIndex: 9999, 
                  width: "var(--radix-select-trigger-width)",
                  minWidth: "var(--radix-select-trigger-width)",
                  maxHeight: "200px"
                 }}
                position="popper"
                sideOffset={5}
              >
                {followUpCountOptions.map((count) => (
                  <SelectItem key={count} value={count.toString()} className={styles.selectItem}>
                    {count}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className={styles.RecommendationLabel} style={{ marginTop: 16 }}>
              {RecommendationLevelText.Recommend}
            </p>
            <div className={styles.options}>
              {recommendationOptions.map((opt) => (
                <label className={styles.radioLabel} key={opt.key}>
                  <input
                    type="radio"
                    name="recommendation"
                    value={opt.key}
                    checked={form.recommendation === opt.key}
                    onChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        recommendation: opt.key,
                      }))
                    }
                  />
                  {opt.value}
                </label>
              ))}
            </div>
            
           
            
            <span className={styles.filterlabels} style={{ marginTop: 16 }}>
              {RecommendationLevelText.Comments}
            </span>
            <textarea
              className={styles.textarea}
              rows={4}
              value={form.recComments}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  recComments: e.target.value,
                }))
              }
            />
          </div>
        )}
      </div>
    </SideDrawerOverlay>
  );
};

export default ReviewOverlay;
